import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import {
  Save, ArrowLeft, Building2, Landmark, Search, ShieldCheck,
  Users, Mail, Phone, Plus, Trash2, Paperclip, ChevronLeft,
  ChevronRight, Type, Minus, Briefcase, Info, MapPin
} from 'lucide-react';

// المكونات المشتركة
import UniversalLookup from './UniversalLookup';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import { useFontSize } from './FontSizeContext';
import SmartPrintButton from './PrintButton';
import { usePermissions } from './usePermissions';
import CustomFieldSlot from './CustomFieldSlot';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

const VendorDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';

  // 1. الإعدادات العامة والخط
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const [activeTab, setActiveTab] = useState('GENERAL');
  const [selectedContactIndex, setSelectedContactIndex] = useState(null);
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionOrg = session.org_id || '';
  const { canCreate, canUpdate, canDelete } = usePermissions('vendors');

  // 2. بيانات المورد (شاملة حقول العنوان الجديدة)
  const [vendorData, setVendorData] = useState({
    comp_num: '',
    name: '',
    type: 'VENDOR',
    currency: 'EGP',
    trn: '',
    bank: '',
    gl_account: '',
    parent: '',
    org_id: sessionOrg,
    address: '', // حقل العنوان الجديد
    city: '',    // حقل المدينة الجديد
    state: '',   // حقل الولاية الجديد
    country: ''  // حقل الدولة الجديد
  });

  // 3. حالات جهات الاتصال والمرفقات والنافيجيشن
  const [contacts, setContacts] = useState([]);
  const [attachCount, setAttachCount] = useState(0);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', domainId: '' });
  const [fieldMaps, setFieldMaps] = useState({});

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  // جلب الميتاداتا
  useEffect(() => {
    const fetchMetadata = async () => {
        try {
            const [orgRes, siteRes] = await Promise.all([
                axios.get('/api/admin/field-map/companies'),
                axios.get('/api/admin/field-map/sites')
            ]);
            setFieldMaps({ ...orgRes.data, ...siteRes.data });
        } catch (e) { console.error("Metadata Load Error"); }
    };
    fetchMetadata();
  }, []);

  // محرك التنقل
  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`/api/companies/${id}/navigation${location.search}`);
      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { console.error("Nav failed"); }
  }, [id, isNew, location.search]);

  // جلب بيانات المورد وجهات الاتصال
  const loadData = useCallback(async () => {
    if (!isNew) {
      try {
        const [vendRes, contRes] = await Promise.all([
          axios.get(`/api/companies/${id}`),
          axios.get(`/api/contacts/COMPANIES/${id}`)
        ]);
        setVendorData(vendRes.data);
        setContacts(contRes.data || []);
        if (contRes.data?.length > 0) setSelectedContactIndex(0); // اختيار أول سطر
        fetchCount(vendRes.data.comp_num);
        loadNavigation();
      } catch (err) { toast.error(t('vendors.details.loadError', 'Error fetching data')); }
    }
  }, [id, isNew, loadNavigation, t]);

  const fetchCount = async (num) => {
    try {
      const res = await axios.get(`/api/attachments/count/COMPANIES/${num}`);
      setAttachCount(res.data.count || 0);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadData(); }, [loadData]);

  const handleInputChange = (field, value) => {
    setVendorData(prev => ({ ...prev, [field]: value }));
  };

  // الحفظ الشامل
  const handleSave = async () => {
    if (!vendorData.comp_num || !vendorData.name) {
      return toast.error(t('vendors.details.requiredFields', 'Company ID and Name are required!'));
    }

    const loadId = toast.loading(t('vendors.details.savingData', 'Saving Data...'));
    try {
      const token = localStorage.getItem('token');
      const payload = { ...vendorData };

      // تنظيف البيانات
      Object.keys(payload).forEach(key => { if (payload[key] === "") payload[key] = null; });

      // 1. حفظ المورد
      await axios.post('/api/companies/save', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // 2. حفظ جهات الاتصال
      const contactsPayload = contacts.map(c => ({
          ...c,
          ownertable: 'COMPANIES',
          ownerid: vendorData.comp_num
      }));

      await axios.post('/api/contacts/save-all', contactsPayload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success(t('common.save_success', 'Saved Successfully'), { id: loadId });
      if (isNew) navigate(`/vendors/${vendorData.comp_num}`);
    } catch (err) {
      // 🎯 تصحيح خطأ React #31 هنا
      let errorMessage = t('common.save_fail', 'Save Failed');

      if (err.response?.status === 422) {
        // لو الخطأ 422، الـ detail بيكون array، لازم نحوله لنص
        errorMessage = t('vendors.details.validationError', 'Validation Error: Please check required fields');
        console.log("Validation Details:", err.response.data.detail);
      } else if (typeof err.response?.data?.detail === 'string') {
        errorMessage = err.response.data.detail;
      }

      toast.error(errorMessage, { id: loadId });
    }
  };

  // الحذف
  const handleDelete = async () => {
    const result = await Swal.fire({
      ...swalTheme(),
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('vendors.details.deleteConfirmText', 'This will delete the company and its contacts!'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!')
    });
    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/companies/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        toast.success(t('common.delete_success', 'Deleted successfully'));
        navigate('/vendors');
      } catch (err) { toast.error(err.response?.data?.detail || t('common.delete_fail', 'Delete failed')); }
    }
  };

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  const handleSendEmail = async () => {
      const firstContactEmail = contacts.length > 0 ? contacts[0].email : null;
      if (!firstContactEmail) return toast.error(t('vendors.details.emailMissing', 'Vendor email is missing!'));
      const result = await Swal.fire({
        ...swalTheme(),
        title: t('vendors.details.sendSlaTitle', 'Send SLA Penality?'), text: t('vendors.details.sendSlaConfirm', 'Do you want to email this request to {{vendor}}?', { vendor: vendorData.comp_num }),
        icon: 'question', showCancelButton: true
      });
      if (result.isConfirmed) {
        try {
          toast.loading(t('vendors.details.sendingEmail', 'Sending Email...'));
          const token = localStorage.getItem('token');
          await axios.post(`/api/vendor/send-email/${vendorData.comp_id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.dismiss(); toast.success(t('vendors.details.emailSent', 'Email sent successfully!'));
        } catch (err) { toast.dismiss(); toast.error(t('vendors.details.emailFailed', 'Email failed')); }
      }
    };
  // إدارة جهات الاتصال
  const addContactRow = () => {
    const newRow = {
      contact_name: '', position: '', email: '', phone: '',
      mobile: '', personal_email: '', address: '', city: '', country: ''
    };
    setContacts([...contacts, newRow]);
    setSelectedContactIndex(contacts.length);
  };

  const handleContactChange = (idx, field, val) => {
    const updated = [...contacts];
    updated[idx][field] = val;
    setContacts(updated);
  };

  const handleSmartSearch = (fieldName, title, defaultType) => {
      const linkedDomain = fieldMaps[fieldName];
      setLookup({
          isOpen: true, title,
          type: linkedDomain ? 'DOMAIN' : defaultType,
          domainId: linkedDomain || '', field: fieldName
      });
  };

  const goTo = (target) => target && navigate(`/vendors/${target}${location.search}`);

  return (
    <div className="flex flex-col h-screen bg-surface-app font-sans overflow-hidden" style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup
        {...lookup}
        onClose={() => setLookup({...lookup, isOpen: false})}
        onSelect={(row) => {
            const val = row.comp_num || row.org_id || row.value || row.id;
            if (activeTab === 'CONTACTS' && selectedContactIndex !== null) {
              handleContactChange(selectedContactIndex, lookup.field, val);
            } else {
              handleInputChange(lookup.field, val);
            }
            setLookup(prev => ({ ...prev, isOpen: false }));
        }}
      />

      {/* --- TOP TOOLBAR --- */}
      <div className="bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/vendors')} className="p-1.5 hover:bg-white/10 rounded-full text-white/70">
            <ArrowLeft size={18} />
          </button>
          {!isNew && (
            <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-status-warning">{nav.current_pos}</span> / {nav.total}</div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} strokeWidth={3}/></button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isNew && <button onClick={handleSendEmail} className="bg-primary hover:bg-primary-hover px-4 py-1.5 rounded-sm flex items-center gap-2 font-black shadow-md"><Mail size={14}/> {t('vendors.details.sendEmail', 'SEND EMAIL')}</button>}
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
          <button onClick={handleSave} className="bg-status-success hover:bg-status-success text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] font-black shadow-md transition-all">
            <Save size={14} /> {t('common.save', 'SAVE')}
          </button>
          )}
          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-white/70" title={t('common.attachments', 'Attachments')}>
            <Paperclip size={16} />
            {attachCount > 0 && <span className="absolute top-1 right-1 bg-status-danger text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{attachCount}</span>}
          </button>
          {((!isNew && canDelete)) && (
          <button onClick={handleDelete} className="p-2 hover:bg-status-danger-bg rounded text-status-danger" title={t('common.delete', 'Delete')}><Trash2 size={16}/></button>
          )}
          <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
            <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-status-warning"><Minus size={14} /></button>
            <div className="flex items-center gap-1 min-w-[40px] justify-center text-white/70">
              <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
            </div>
            <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-status-warning"><Plus size={14} /></button>
          </div>
          {!isNew && (
          <SmartPrintButton
            moduleName="VENDOR"
            refId={`${vendorData.comp_num}`}
           // تمرير البارامتر المطلوب
          />)}
        </div>
      </div>

      {/* --- HEADER FIELDS --- */}
      <div className="bg-surface-card border-b border-line px-6 py-3 flex items-center gap-12 shadow-sm shrink-0 z-20">
        <div className="flex flex-col border-r pr-8 border-line-subtle">
          <label className="text-[8px] text-ink-secondary font-black uppercase italic tracking-widest leading-none mb-1">{t('vendors.fields.company', 'Company')}</label>
          <input
            className={`w-24 border-b border-transparent outline-none font-black text-primary uppercase bg-transparent ${!isNew ? 'opacity-60' : 'focus:border-primary'}`}
            value={vendorData.comp_num}
            disabled={!isNew}
            onChange={(e) => handleInputChange('comp_num', e.target.value.toUpperCase())}
            placeholder={t('vendors.details.newPlaceholder', 'NEW')}
          />
        </div>
        <div className="flex flex-col flex-1">
          <label className="text-[8px] text-ink-secondary font-black uppercase italic tracking-widest leading-none mb-1">{t('vendors.fields.companyDescription', 'Company Description')}</label>
          <input
            className="w-full border-b border-transparent outline-none text-primary font-bold bg-transparent focus:border-primary transition-all"
            value={vendorData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder={t('vendors.details.namePlaceholder', 'Enter Company Name...')}
          />
        </div>
      </div>

      {/* --- TABS --- */}
      <div className="flex bg-surface-card border-b border-line-subtle px-4 shrink-0 font-black uppercase tracking-tighter">
        <button onClick={() => setActiveTab('GENERAL')}
          className={`px-8 py-3 flex items-center gap-2 transition-all ${activeTab === 'GENERAL' ? 'border-b-2 border-primary-dark text-primary-dark' : 'text-ink-secondary hover:text-ink'}`}>
          <Briefcase size={13}/> {t('vendors.details.tabDetails', 'Vendor Details')}
        </button>
        <button onClick={() => setActiveTab('CONTACTS')}
          className={`px-8 py-3 flex items-center gap-2 transition-all ${activeTab === 'CONTACTS' ? 'border-b-2 border-primary-dark text-primary-dark' : 'text-ink-secondary hover:text-ink'}`}>
          <Users size={13}/> {t('vendors.details.tabContacts', 'Contact Persons')}
        </button>
      </div>

      {/* --- CONTENT AREA --- */}
      <div className="flex-1 overflow-y-auto p-4 bg-surface-panel custom-scrollbar">
        {activeTab === 'GENERAL' ? (
          <div className="flex flex-col gap-6 max-w-7xl mx-auto animate-in fade-in duration-500 pb-10">
            <div className="grid grid-cols-12 gap-6">
                <div className="col-span-8">
                  <SectionCard title={t('vendors.details.companyInformation', 'Company Information')}>
                    <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                      <div className="flex flex-col border-b border-line pb-1">
                        <label className="text-[8px] font-black text-ink-secondary uppercase italic">{t('vendors.fields.companyType', 'Company Type')}</label>
                        <select className="font-bold text-primary outline-none bg-transparent" value={vendorData.type} onChange={(e) => handleInputChange('type', e.target.value)}>
                          <option value="VENDOR">{t('vendors.fields.vendorSupplier', 'Vendor (Supplier)')}</option>
                          <option value="MANUFACTURER">{t('vendors.fields.manufacturer', 'Manufacturer')}</option>
                          <option value="INTERNAL">{t('vendors.fields.internalCompany', 'Internal Company')}</option>
                        </select>
                      </div>
                      <MaximoLookupField label={t('vendors.fields.parentCompany', 'Parent Company')} value={vendorData.parent} onSearch={() => handleSmartSearch('parent', t('vendors.lookup.parentCompanies', 'Parent Companies'), 'vendors')} />
                      <MaximoInput label={t('vendors.fields.trn', 'Tax Registration (TRN)')} value={vendorData.trn} onChange={(v) => handleInputChange('trn', v)} />
                      <MaximoLookupField label={t('common.org', 'Organization')} value={vendorData.org_id} onSearch={() => handleSmartSearch('org_id', t('menu.organization', 'Organizations'), 'organizations')} />
                      <CustomFieldSlot coreModelName="VENDOR" tab="GENERAL" section="Company Information" recordId={vendorData.comp_num} disabled={isNew} />
                    </div>
                  </SectionCard>
                </div>
                <div className="col-span-4">
                  <SectionCard title={t('vendors.details.financials', 'Financials')}>
                    <div className="space-y-4">
                      <MaximoLookupField label={t('vendors.fields.currency', 'Currency')} value={vendorData.currency} onSearch={() => handleSmartSearch('currency', t('vendors.lookup.currencies', 'Currencies'), 'DOMAIN')} />
                      <MaximoInput label={t('vendors.fields.bankName', 'Bank Name')} value={vendorData.bank} onChange={(v) => handleInputChange('bank', v)} />
                      <MaximoInput label={t('vendors.fields.glAccount', 'GL Account')} value={vendorData.gl_account} onChange={(v) => handleInputChange('gl_account', v)} />
                      <CustomFieldSlot coreModelName="VENDOR" tab="GENERAL" section="Financials" recordId={vendorData.comp_num} disabled={isNew} />
                    </div>
                  </SectionCard>
                </div>
            </div>

            {/* قسم العنوان الخاص بالشركة */}
            <SectionCard title={t('vendors.details.addressDetails', 'Primary Address & Location Details')}>
                <div className="grid grid-cols-4 gap-8">
                    <MaximoInput label={t('vendors.fields.streetAddress', 'Street Address')} value={vendorData.address} onChange={(v) => handleInputChange('address', v)} />
                    <MaximoInput label={t('vendors.fields.city', 'City')} value={vendorData.city} onChange={(v) => handleInputChange('city', v)} />
                    <MaximoInput label={t('vendors.fields.state', 'State / Province')} value={vendorData.state} onChange={(v) => handleInputChange('state', v)} />
                    <MaximoLookupField label={t('vendors.fields.country', 'Country')} value={vendorData.country} onSearch={() => handleSmartSearch('country', t('vendors.lookup.selectCountry', 'Select Country'), 'DOMAIN')} />
                    <CustomFieldSlot coreModelName="VENDOR" tab="GENERAL" section="Address Details" recordId={vendorData.comp_num} disabled={isNew} />
                </div>
            </SectionCard>
          </div>
        ) : (
          <div className="flex flex-col gap-4 max-w-7xl mx-auto animate-in slide-in-from-right duration-500 h-full pb-20">
            {/* جدول جهات الاتصال */}
            <div className="bg-surface-card border border-line rounded shadow-lg overflow-hidden shrink-0">
              <div className="p-2 bg-surface-panel border-b border-line flex justify-between items-center">
                <span className="text-[9px] font-black text-ink-secondary uppercase tracking-widest italic flex items-center gap-2">
                  <Users size={14}/> {t('vendors.details.masterContactList', 'Master Contact List')}
                </span>
                <button onClick={addContactRow} className="bg-primary-dark text-white px-3 py-1 rounded-sm text-[9px] font-black flex items-center gap-1 hover:bg-primary transition-all">
                  <Plus size={12}/> {t('vendors.details.addContact', 'ADD CONTACT')}
                </button>
              </div>
              <div className="max-h-[250px] overflow-y-auto">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-surface-panel border-b border-line font-black uppercase text-ink-secondary sticky top-0 z-10">
                      <tr>
                        <th className="p-3 border-r border-line-subtle w-10 text-center">#</th>
                        <th className="p-3 border-r border-line-subtle">{t('vendors.fields.contactName', 'Contact Name')}</th>
                        <th className="p-3 border-r border-line-subtle">{t('vendors.fields.position', 'Position')}</th>
                        <th className="p-3 border-r border-line-subtle">{t('vendors.fields.officialEmail', 'Official Email')}</th>
                        <th className="p-3 w-10 text-center text-status-danger"><Trash2 size={12}/></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line-subtle">
                      {contacts.length === 0 ? (
                        <tr><td colSpan="5" className="p-10 text-center text-ink-secondary italic">{t('vendors.details.noContacts', 'No contacts defined.')}</td></tr>
                      ) : contacts.map((c, idx) => (
                        <tr key={idx}
                          onClick={() => setSelectedContactIndex(idx)}
                          className={`cursor-pointer transition-colors ${selectedContactIndex === idx ? 'bg-primary-soft/50' : 'hover:bg-primary-soft'}`}>
                          <td className="p-3 border-r border-line-subtle text-center font-bold text-ink-secondary">{idx + 1}</td>
                          <td className="p-1 border-r border-line-subtle"><input className="w-full bg-transparent outline-none p-2 font-bold text-primary uppercase" value={c.contact_name || ''} onChange={e => handleContactChange(idx, 'contact_name', e.target.value)} /></td>
                          <td className="p-1 border-r border-line-subtle"><input className="w-full bg-transparent outline-none p-2 italic text-ink" value={c.position || ''} onChange={e => handleContactChange(idx, 'position', e.target.value)} /></td>
                          <td className="p-1 border-r border-line-subtle"><input className="w-full bg-transparent outline-none p-2 text-primary" value={c.email || ''} onChange={e => handleContactChange(idx, 'email', e.target.value)} /></td>
                          <td className="p-1 text-center">
                            <button onClick={(e) => { e.stopPropagation(); setContacts(contacts.filter((_, i) => i !== idx)); }} className="p-2 text-ink-secondary hover:text-status-danger"><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </div>
            </div>

            {/* قسم تفاصيل العنوان والاتصال للشخص المختار (Master-Detail) */}
            {selectedContactIndex !== null && contacts[selectedContactIndex] && (
              <div className="animate-in slide-in-from-bottom duration-300">
                <SectionCard title={`${t('vendors.details.extendedDetails', 'Extended Details')}: ${contacts[selectedContactIndex].contact_name || t('vendors.details.newMember', 'New Member')}`}>
                  <div className="grid grid-cols-4 gap-x-8 gap-y-6">
                    <MaximoInput label={t('vendors.fields.workPhone', 'Work Phone')} value={contacts[selectedContactIndex].phone} onChange={(v) => handleContactChange(selectedContactIndex, 'phone', v)} />
                    <MaximoInput label={t('vendors.fields.mobile', 'Mobile / WhatsApp')} value={contacts[selectedContactIndex].mobile} onChange={(v) => handleContactChange(selectedContactIndex, 'mobile', v)} />
                    <MaximoInput label={t('vendors.fields.personalEmail', 'Personal Email')} value={contacts[selectedContactIndex].personal_email} onChange={(v) => handleContactChange(selectedContactIndex, 'personal_email', v)} />
                    <MaximoLookupField label={t('vendors.fields.country', 'Country')} value={contacts[selectedContactIndex].country} onSearch={() => handleSmartSearch('country', t('vendors.lookup.selectCountry', 'Select Country'), 'DOMAIN')} />
                    <div className="col-span-2">
                        <MaximoInput label={t('vendors.fields.fullAddress', 'Full Address')} value={contacts[selectedContactIndex].address} onChange={(v) => handleContactChange(selectedContactIndex, 'address', v)} />
                    </div>
                    <MaximoInput label={t('vendors.fields.city', 'City')} value={contacts[selectedContactIndex].city} onChange={(v) => handleContactChange(selectedContactIndex, 'city', v)} />
                    <div className="bg-status-warning-bg p-3 rounded border border-status-warning/20 flex items-center gap-3">
                        <Info size={16} className="text-status-warning" />
                        <p className="text-[9px] text-status-warning font-bold uppercase italic tracking-tighter">{t('vendors.details.contactRowInfo', 'Information linked to selected contact row above.')}</p>
                    </div>
                  </div>
                </SectionCard>
              </div>
            )}
          </div>
        )}
      </div>

      {isDocModalOpen && (
        <AttachmentsModal isOpen={isDocModalOpen} ownertable="COMPANIES" ownerid={vendorData.comp_num} onClose={() => {setIsDocModalOpen(false); fetchCount(vendorData.comp_num);}} />
      )}
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="companies" recordId={vendorData.comp_num} />
    </div>
  );
};

// --- Reusable Components (Visual Style Consistency) ---
const SectionCard = ({ title, children }) => (
  <div className="bg-surface-card border border-line rounded shadow-sm overflow-hidden transition-all hover:shadow-md shrink-0">
    <div className="p-2 bg-surface-panel border-b border-line font-black text-ink-secondary uppercase text-[9px] tracking-widest flex items-center gap-2 italic">
      <div className="w-1.5 h-3 bg-primary-dark rounded-full"></div> {title}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const MaximoInput = ({ label, value, onChange }) => (
  <div className="flex flex-col border-b border-line py-1 focus-within:border-primary transition-all">
    <label className="text-[8px] font-black text-ink-secondary uppercase italic leading-none mb-1">{label}</label>
    <input
        className="font-bold text-primary outline-none bg-transparent py-0.5"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const MaximoLookupField = ({ label, value, onSearch }) => (
  <div className="flex flex-col border-b border-line py-1 focus-within:border-primary transition-all">
    <label className="text-[8px] font-black text-ink-secondary uppercase italic leading-none mb-1">{label}</label>
    <div className="flex items-center justify-between pb-0.5 h-6">
      <span className="font-black text-primary uppercase truncate">{value || '---'}</span>
      <Search size={12} className="text-primary cursor-pointer hover:scale-110 transition-transform" onClick={onSearch} />
    </div>
  </div>
);

export default VendorDetails;
